export function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export function escapeAttr(value: string): string {
  return escapeHtml(value);
}

export function primaryButton(id: string, label: string, type: "button" | "submit" = "button"): string {
  return `<button type="${type}" class="btn-primary" id="${escapeAttr(id)}">${escapeHtml(label)}</button>`;
}

export function dangerButton(id: string, label: string): string {
  return `<button type="button" class="btn-danger" id="${escapeAttr(id)}">${escapeHtml(label)}</button>`;
}

export const ICONS = {
  caret: `<svg class="ico-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>`,
  back: `<svg class="ico-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>`,
  check: `<svg class="ico-svg ico-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M5 12l5 5L20 7"/></svg>`,
  clock: `<svg class="ico-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>`,
  x: `<svg class="ico-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M7 7l10 10M17 7L7 17"/></svg>`,
  external: `<svg class="ico-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 5h5v5M19 5l-9 9"/><path d="M19 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5"/></svg>`,
};

export function paymentShell(opts: {
  title: string;
  subtitle?: string;
  body: string;
  logoUrl?: string | null;
  brandName?: string | null;
}): string {
  const customLogo = Boolean(opts.logoUrl);
  const logo = opts.logoUrl || "/logo.svg";
  const brand = opts.brandName || "Flowgate";
  return `
    <div class="page">
      <div class="shell">
        <div class="pay-card">
          <header class="pay-head">
            <div class="pay-brand">
              <img class="pay-logo${customLogo ? " is-custom" : ""}" src="${escapeAttr(logo)}" alt="${escapeAttr(brand)}" />
              ${customLogo ? "" : `<span class="pay-brand-name">${escapeHtml(brand)}</span>`}
            </div>
            <div class="pay-head-text">
              <p class="pay-head-title">${escapeHtml(opts.title)}</p>
              ${opts.subtitle ? `<p class="pay-head-sub">${escapeHtml(opts.subtitle)}</p>` : ""}
            </div>
          </header>
          <div class="pay-body">${opts.body}</div>
        </div>
      </div>
    </div>`;
}

export function loadingShell(message: string): string {
  return `
    <div class="page">
      <div class="shell">
        <div class="pay-card pay-card-loading">
          <div class="pay-body">
            <div class="loading-row"><span class="spinner"></span><span>${escapeHtml(message)}</span></div>
          </div>
        </div>
      </div>
    </div>`;
}

export function amountHero(amount: string, caption: string): string {
  return `
    <div class="amount-hero">
      <p class="amount-value">${escapeHtml(amount)}</p>
      <p class="amount-caption">${escapeHtml(caption)}</p>
    </div>`;
}

export function summaryList(rows: Array<{ label: string; value: string }>): string {
  if (!rows.length) return "";
  return `<dl class="summary">${rows
    .map(
      (row) =>
        `<div class="summary-row"><dt>${escapeHtml(row.label)}</dt><dd>${escapeHtml(row.value)}</dd></div>`
    )
    .join("")}</dl>`;
}

export function stepDots(total: number, current: number): string {
  const dots = Array.from({ length: total }, (_, i) => {
    const on = i < current ? " is-on" : "";
    return `<span class="step-dot${on}"></span>`;
  }).join("");
  return `<div class="step-dots">${dots}</div>`;
}

export function chip(iconUrl: string, label: string): string {
  return `<span class="chip"><img src="${escapeAttr(iconUrl)}" alt="" />${escapeHtml(label)}</span>`;
}

export function chipRow(items: Array<{ icon: string; label: string }>): string {
  if (!items.length) return "";
  return `<div class="chip-row">${items
    .map((item, i) => `${i ? `<span class="chip-sep">${ICONS.caret}</span>` : ""}${chip(item.icon, item.label)}`)
    .join("")}</div>`;
}

export function backButton(id: string, label: string): string {
  return `<button type="button" class="back-btn" id="${escapeAttr(id)}">${ICONS.back} Back to ${escapeHtml(label)}</button>`;
}

export function pickButton(opts: {
  key: string;
  value: string;
  icon?: string;
  iconHtml?: string;
  title: string;
  subtitle?: string;
  iconClass?: string;
}): string {
  const media =
    opts.iconHtml ||
    `<img class="pick-ico${opts.iconClass ? ` ${opts.iconClass}` : ""}" src="${escapeAttr(opts.icon || "")}" alt="" />`;
  return `
    <button type="button" class="pick" data-${escapeAttr(opts.key)}="${escapeAttr(opts.value)}">
      ${media}
      <span class="pick-text">
        <strong>${escapeHtml(opts.title)}</strong>
        ${opts.subtitle ? `<small>${escapeHtml(opts.subtitle)}</small>` : ""}
      </span>
      ${ICONS.caret}
    </button>`;
}

export function errorBox(message: string): string {
  return `<div class="msg msg-danger">${escapeHtml(message)}</div>`;
}

export function infoBox(message: string): string {
  return `<div class="msg msg-info">${escapeHtml(message)}</div>`;
}

export function hint(message: string): string {
  return `<p class="hint">${escapeHtml(message)}</p>`;
}

export function textField(opts: {
  id: string;
  label: string;
  type?: string;
  value?: string;
  placeholder?: string;
  autocomplete?: string;
  required?: boolean;
}): string {
  const type = opts.type || "text";
  const required = opts.required ? " required" : "";
  return `
    <div class="field">
      <label for="${escapeAttr(opts.id)}">${escapeHtml(opts.label)}</label>
      <input
        id="${escapeAttr(opts.id)}"
        name="${escapeAttr(opts.id)}"
        type="${escapeAttr(type)}"
        value="${escapeAttr(opts.value || "")}"
        placeholder="${escapeAttr(opts.placeholder || "")}"
        autocomplete="${escapeAttr(opts.autocomplete || "on")}"
        ${required}
      />
    </div>`;
}

export function loadingRow(label: string): string {
  return `<div class="loading-row"><span class="spinner"></span><span>${escapeHtml(label)}</span></div>`;
}

export function successBlock(opts: {
  amount: string;
  caption: string;
  title: string;
  subtitle: string;
  chips?: string;
}): string {
  return `
    <div class="success">
      <div class="success-mark">${ICONS.check}</div>
      ${amountHero(opts.amount, opts.caption)}
      ${opts.chips || ""}
      <h2 class="success-title">${escapeHtml(opts.title)}</h2>
      <p class="success-sub">${escapeHtml(opts.subtitle)}</p>
    </div>`;
}

export function networkLabel(name: string, shortName: string, testnet?: boolean): string {
  const title = name.trim();
  const short = shortName.trim();
  const base = title && short && title !== short ? `${title} (${short})` : title || short;
  return testnet ? `${base}` : base;
}
